import React, { useState, useEffect, useRef } from 'react';
import { Search, ChevronDown, ChevronRight, X, Upload, Plus, Trash2, Edit2 } from 'lucide-react';
import axios from 'axios';
import { CPTreeNode } from './CPTreeNode';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const BRAND = '#1a3c2e';

const LEVELS = [
  { label: 'DM', bg: '#1a3c2e' }, { label: 'ADM', bg: '#1565c0' },
  { label: 'CP1', bg: '#2e7d32' }, { label: 'CP2', bg: '#e65100' },
  { label: 'CP3', bg: '#6a1b9a' }, { label: 'CP4', bg: '#00695c' },
  { label: 'CP5', bg: '#ad1457' }, { label: 'CP6', bg: '#4527a0' },
];

function getLevelColor(level) {
  const found = LEVELS.find(function(l) { return l.label === level; });
  if (found) return { bg: found.bg, light: found.bg + '22', text: found.bg };
  return { bg: '#37474f', light: '#eceff1', text: '#37474f' };
}

function CPStructureTab({ refreshKey, onImportClick }) {
  const [hierarchy, setHierarchy] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedDMs, setExpandedDMs] = useState({});
  const [forceOpen, setForceOpen] = useState(0);

  // Search state
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSug, setShowSug] = useState(false);
  const [searching, setSearching] = useState(false);
  const [result, setResult] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const dropRef = useRef(null);

  // Edit mode state
  const [editMode, setEditMode] = useState(false);
  const [editModal, setEditModal] = useState(null);
  const [editName, setEditName] = useState('');
  const [addModal, setAddModal] = useState(null);
  const [newName, setNewName] = useState('');
  const [removeModal, setRemoveModal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toastMsg, setToastMsg] = useState('');
  const showToast = function(msg) { setToastMsg(msg); setTimeout(function() { setToastMsg(''); }, 3000); };

  const openEdit = function(name, dmName, level) { setEditModal({ name: name, dmName: dmName, level: level }); setEditName(name); };

  const handleEdit = async function() {
    if (!editModal || !editName.trim() || editName.trim() === editModal.name) { setEditModal(null); return; }
    setSaving(true);
    try {
      await axios.put(API + '/cp-tracker/hierarchy/edit', {
        old_name: editModal.name, new_name: editName.trim(), dm_name: editModal.dmName,
      });
      showToast('Renamed "' + editModal.name + '" to "' + editName.trim() + '"');
      setEditModal(null); setEditName('');
      fetchHierarchy();
    } catch (e) { showToast('Error: ' + ((e.response && e.response.data && e.response.data.detail) || 'Failed')); }
    setSaving(false);
  };

  const handleAdd = async function() {
    if (!addModal || !newName.trim()) return;
    setSaving(true);
    try {
      await axios.post(API + '/cp-tracker/hierarchy/add', {
        parent_name: addModal.parentName, new_name: newName.trim(), dm_name: addModal.dmName,
      });
      showToast('Added "' + newName.trim() + '" under "' + addModal.parentName + '"');
      setAddModal(null); setNewName('');
      fetchHierarchy();
    } catch (e) { showToast('Error: ' + ((e.response && e.response.data && e.response.data.detail) || 'Failed')); }
    setSaving(false);
  };

  const handleRemove = async function() {
    if (!removeModal) return;
    setSaving(true);
    try {
      await axios.delete(API + '/cp-tracker/hierarchy/remove', {
        data: { name: removeModal.name, dm_name: removeModal.dmName },
      });
      showToast('Removed "' + removeModal.name + '"');
      setRemoveModal(null);
      fetchHierarchy();
    } catch (e) { showToast('Error: ' + ((e.response && e.response.data && e.response.data.detail) || 'Failed')); }
    setSaving(false);
  };

  function fetchHierarchy() {
    setLoading(true);
    axios.get(API + '/cp-tracker/hierarchy')
      .then(function(r) { setHierarchy(r.data.hierarchy || []); })
      .finally(function() { setLoading(false); });
  }

  useEffect(function() {
    fetchHierarchy();
  }, [refreshKey]);

  useEffect(function() {
    if (!query || query.length < 2) { setSuggestions([]); return; }
    const t = setTimeout(async function() {
      try {
        const r = await axios.get(API + '/cp-tracker/name-suggestions', { params: { q: query } });
        setSuggestions(r.data.suggestions || []);
        setShowSug(true);
      } catch (e) { setSuggestions([]); }
    }, 250);
    return function() { clearTimeout(t); };
  }, [query]);

  useEffect(function() {
    function h(e) { if (dropRef.current && !dropRef.current.contains(e.target)) setShowSug(false); }
    document.addEventListener('mousedown', h);
    return function() { document.removeEventListener('mousedown', h); };
  }, []);

  const handleSearch = async function(name) {
    const q = (name || query).trim();
    if (!q) return;
    setQuery(q); setShowSug(false); setSuggestions([]);
    setSearching(true); setNotFound(false); setResult(null);
    try {
      const [upper, down] = await Promise.all([
        axios.get(API + '/cp-tracker/upper-line', { params: { name: q } }),
        axios.get(API + '/cp-tracker/down-line', { params: { name: q } }),
      ]);
      if (upper.data.found) {
        const chain = upper.data.chain || [];
        setResult({ name: chain[0] ? chain[0].name : q, level: chain[0] ? chain[0].level : '', upperChain: [...chain].reverse(), downData: down.data.found ? down.data : null });
      } else setNotFound(true);
    } catch (e) { setNotFound(true); }
    setSearching(false);
  };

  const clearSearch = function() { setQuery(''); setResult(null); setNotFound(false); setSuggestions([]); };
  const toggleDM = function(n) { setExpandedDMs(function(p) { return Object.assign({}, p, { [n]: !p[n] }); }); };

  if (loading) return <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: BRAND }}></div></div>;

  return (
    <div className="space-y-4">
      {/* Toolbar: Search + Import */}
      <div className="flex items-center gap-3">
        {/* Search Bar */}
        <div className="bg-white rounded-xl border border-stone-200 p-4 shadow-sm flex-1" ref={dropRef}>
          <div className="flex gap-3 relative">
            <div className="flex-1 relative">
              <div className="flex items-center gap-2 border border-stone-300 rounded-lg px-3 py-2.5 focus-within:ring-2 bg-white" style={{ '--tw-ring-color': BRAND }}>
                <Search className="w-4 h-4 text-stone-400 flex-shrink-0" />
                <input
                  className="flex-1 text-sm outline-none text-stone-800 placeholder-stone-400"
                  placeholder="Search DM / ADM / CP name to see upper line + down line..."
                  value={query}
                  onChange={function(e) { setQuery(e.target.value); setShowSug(true); }}
                  onKeyDown={function(e) { if (e.key === 'Enter') handleSearch(); if (e.key === 'Escape') setShowSug(false); }}
                  onFocus={function() { if (suggestions.length > 0) setShowSug(true); }}
                  data-testid="cp-structure-search"
                  autoComplete="off"
                />
                {query && <button onClick={clearSearch} className="text-stone-300 hover:text-stone-500"><X className="w-3.5 h-3.5" /></button>}
              </div>
              {showSug && suggestions.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl border border-stone-200 shadow-xl z-50 overflow-hidden max-h-52 overflow-y-auto">
                  {suggestions.map(function(s, i) {
                    const sn = typeof s === 'string' ? s : s.name;
                    const sl = typeof s === 'string' ? '' : s.level;
                    const clr = getLevelColor(sl);
                    return (
                      <button key={i} onClick={function() { handleSearch(sn); }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-stone-50 text-left transition-colors">
                        {sl && <span className="text-xs font-bold px-1.5 py-0.5 rounded text-white flex-shrink-0" style={{ background: clr.bg }}>{sl}</span>}
                        <span className="text-sm text-stone-700">{sn}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <button onClick={function() { handleSearch(); }} disabled={searching || !query}
              className="px-5 py-2.5 rounded-lg text-sm font-semibold text-white transition-colors disabled:opacity-50"
              style={{ background: BRAND }} data-testid="cp-structure-search-btn">
              {searching ? 'Searching…' : 'Search'}
            </button>
            {result && <button onClick={clearSearch} className="px-4 py-2.5 rounded-lg text-sm font-medium bg-stone-100 text-stone-600 hover:bg-stone-200 transition-colors flex items-center gap-1.5"><X className="w-3.5 h-3.5" /> Clear</button>}
          </div>
        </div>
        {/* Edit Mode toggle */}
        <button onClick={function() { setEditMode(function(e) { return !e; }); }}
          className={'flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors shrink-0 ' + (editMode ? 'bg-amber-500 text-white hover:bg-amber-600' : 'bg-stone-100 text-stone-600 hover:bg-stone-200')}
          data-testid="cp-structure-edit-toggle">
          <Edit2 className="w-4 h-4" /> {editMode ? 'Edit Mode ON' : 'Edit'}
        </button>
        {/* Import CP List button */}
        {onImportClick && (
          <button onClick={onImportClick}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold bg-emerald-700 text-white hover:bg-emerald-800 transition-colors shadow-sm shrink-0"
            data-testid="cp-structure-import-btn">
            <Upload className="w-4 h-4" /> Import New CP List
          </button>
        )}
      </div>

      {/* Not found */}
      {notFound && !searching && (
        <div className="bg-white rounded-xl border border-red-200 p-6 text-center shadow-sm">
          <p className="text-stone-500 text-sm">No match for <strong>"{query}"</strong>. Try a partial name.</p>
        </div>
      )}

      {/* Search result: Upper Line + Down Line combined */}
      {result && !searching && (
        <div className="bg-white rounded-xl border border-stone-200 shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-stone-100 flex items-center justify-between" style={{ background: BRAND + '11' }}>
            <div className="flex items-center gap-2">
              <span className="text-sm font-bold text-stone-800">Structure for:</span>
              <span className="font-bold text-sm px-2 py-0.5 rounded text-white" style={{ background: getLevelColor(result.level).bg }}>{result.level}</span>
              <span className="font-bold text-stone-900">{result.name}</span>
            </div>
          </div>

          <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-stone-100">
            {/* Upper Line */}
            <div className="p-5">
              <p className="text-xs font-bold text-stone-400 uppercase tracking-widest mb-3">Upper Line (Who manages them)</p>
              <div className="flex flex-col items-start gap-0.5">
                {result.upperChain.map(function(item, i) {
                  const clr = getLevelColor(item.level);
                  const isSearched = i === result.upperChain.length - 1;
                  return (
                    <div key={i} className="flex flex-col items-start w-full">
                      <div className="flex items-center gap-3 rounded-xl px-4 py-2.5 border-2 w-full"
                        style={{ borderColor: isSearched ? clr.bg : clr.light, background: isSearched ? clr.bg + '15' : '#fafaf9' }}>
                        <span className="text-xs font-bold px-2 py-0.5 rounded text-white flex-shrink-0" style={{ background: clr.bg }}>{item.level}</span>
                        <span className={'text-sm flex-1 ' + (isSearched ? 'font-bold text-stone-900' : 'font-medium text-stone-600')}>{item.name}</span>
                        {isSearched && <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ background: clr.bg + '22', color: clr.bg }}>Searched</span>}
                      </div>
                      {i < result.upperChain.length - 1 && (
                        <div className="ml-6 py-0.5"><ChevronDown className="w-3.5 h-3.5 text-stone-300" /></div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Down Line */}
            <div className="p-5">
              <p className="text-xs font-bold text-stone-400 uppercase tracking-widest mb-3">Down Line (Who is under them)</p>
              {result.downData && result.downData.node && result.downData.node.children && result.downData.node.children.length > 0 ? (
                <div className="space-y-1">
                  {result.downData.node.children.map(function(child, i) {
                    return React.createElement(CPTreeNode, { key: i, node: child, depth: 1, forceOpen: 1, canEdit: false, dmName: '' });
                  })}
                </div>
              ) : (
                <p className="text-xs text-stone-400 py-4 text-center">No one is under {result.name} (leaf node)</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Full Hierarchy Tree (always shown, hidden only when result active) */}
      {!result && !notFound && (
        <>
          {/* Legend + controls */}
          <div className="bg-white rounded-xl border border-stone-200 p-4 shadow-sm flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 flex-wrap">
              {LEVELS.map(function(l) {
                return <span key={l.label} className="text-xs font-bold px-2 py-1 rounded text-white" style={{ background: l.bg }}>{l.label}</span>;
              })}
              <span className="text-xs text-stone-400 mx-1">→ deeper levels continue</span>
            </div>
            <div className="ml-auto flex gap-2">
              <button onClick={function() { setForceOpen(1); }} className="text-xs px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 font-medium hover:bg-emerald-100 transition-colors">Expand All</button>
              <button onClick={function() { setForceOpen(0); }} className="text-xs px-3 py-1.5 rounded-lg bg-stone-100 text-stone-600 font-medium hover:bg-stone-200 transition-colors">Collapse All</button>
            </div>
          </div>

          {/* DM Cards */}
          {hierarchy.map(function(dm) {
            const isOpen = expandedDMs[dm.name];
            const isTeamGroup = !!dm.teamLead;  // e.g. GREEN YUVA has teamLead=BHAVANA PAGARE
            return (
              <div key={dm.name} className="bg-white rounded-xl border border-stone-200 shadow-sm overflow-hidden">
                <div className="flex items-center px-5 py-4 hover:bg-emerald-50 transition-colors cursor-pointer" onClick={function() { toggleDM(dm.name); }} data-testid={'dm-row-' + dm.name}>
                  <div className="flex items-center gap-3 flex-1">
                    {isOpen ? <ChevronDown className="w-4 h-4 text-stone-400" /> : <ChevronRight className="w-4 h-4 text-stone-400" />}
                    {isTeamGroup ? (
                      <div className="w-12 h-9 rounded-lg flex items-center justify-center text-white text-[10px] font-bold leading-tight px-1 text-center" style={{ background: '#065f46' }}>TEAM</div>
                    ) : (
                      <div className="w-9 h-9 rounded-lg flex items-center justify-center text-white text-xs font-bold" style={{ background: BRAND }}>DM</div>
                    )}
                    <div>
                      <p className="font-semibold text-stone-800">{dm.name}</p>
                      {isTeamGroup ? (
                        <p className="text-xs text-stone-400">
                          <span className="font-medium text-emerald-700">TL: {dm.teamLead}</span>
                          {' · '}{dm.admCount || 0} DMs · {dm.cpCount || 0}+ members
                        </p>
                      ) : (
                        <p className="text-xs text-stone-400">{dm.admCount || 0} ADMs · {dm.cpCount || 0}+ CPs</p>
                      )}
                    </div>
                  </div>
                  {editMode && !isTeamGroup && (
                    <div className="flex items-center gap-2 ml-3" onClick={function(e) { e.stopPropagation(); }}>
                      <button onClick={function() { openEdit(dm.name, dm.name, 'DM'); }}
                        className="w-7 h-7 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 flex items-center justify-center transition-colors"
                        title={'Rename ' + dm.name} data-testid={'edit-dm-' + dm.name}>
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button onClick={function() { setAddModal({ parentName: dm.name, dmName: dm.name }); }}
                        className="w-7 h-7 rounded-lg bg-emerald-50 text-emerald-600 hover:bg-emerald-100 flex items-center justify-center transition-colors"
                        title={'Add ADM under ' + dm.name} data-testid={'add-adm-' + dm.name}>
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
                {isOpen && (
                  <div className="border-t border-stone-100 px-5 py-3 space-y-1">
                    {(dm.adms || dm.children || []).length === 0 ? (
                      <p className="text-stone-400 text-sm italic py-2">{isTeamGroup ? 'No DMs assigned' : 'No ADMs assigned'}</p>
                    ) : (
                      (dm.adms || dm.children || []).map(function(adm, i) {
                        return React.createElement(CPTreeNode, { key: i, node: adm, depth: 0, forceOpen: forceOpen, canEdit: editMode && !isTeamGroup, dmName: dm.name, isTeamGroup: isTeamGroup,
                          onAddChild: function(pName, dmN) { setAddModal({ parentName: pName, dmName: dmN }); },
                          onEdit: openEdit,
                          onRemove: function(n, dmN, lv) { setRemoveModal({ name: n, dmName: dmN, level: lv }); } });
                      })
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}

      {/* Toast */}
      {toastMsg && <div className="fixed top-5 right-5 z-[60] bg-stone-900 text-white text-sm px-4 py-2.5 rounded-xl shadow-lg" data-testid="cp-structure-toast">{toastMsg}</div>}

      {/* EDIT / RENAME MODAL */}
      {editModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-stone-800">Rename {editModal.level}</h3>
              <button onClick={function() { setEditModal(null); setEditName(''); }} className="text-stone-400 hover:text-stone-600"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-stone-500 mb-4">
              Editing: <strong className="text-stone-700">{editModal.name}</strong>
              {editModal.level !== 'DM' && <><br /><span className="text-xs text-stone-400">DM: {editModal.dmName}</span></>}
            </p>
            <input
              className="w-full border border-stone-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500 mb-4"
              placeholder="Enter new name…" value={editName} onChange={function(e) { setEditName(e.target.value); }}
              onKeyDown={function(e) { if (e.key === 'Enter') handleEdit(); }} autoFocus data-testid="edit-name-input" />
            <div className="flex gap-3">
              <button onClick={function() { setEditModal(null); setEditName(''); }}
                className="flex-1 py-2.5 rounded-xl text-sm font-medium border border-stone-200 text-stone-600 hover:bg-stone-50">Cancel</button>
              <button onClick={handleEdit} disabled={saving || !editName.trim() || editName.trim() === editModal.name}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                data-testid="confirm-edit-btn">
                {saving ? 'Saving…' : 'Save Name'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ADD MODAL */}
      {addModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-stone-800">Add New Entry</h3>
              <button onClick={function() { setAddModal(null); setNewName(''); }} className="text-stone-400 hover:text-stone-600"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-stone-500 mb-4">
              Adding under: <strong className="text-stone-700">{addModal.parentName}</strong><br />
              <span className="text-xs text-stone-400">DM: {addModal.dmName}</span>
            </p>
            <input
              className="w-full border border-stone-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-emerald-500 mb-4"
              placeholder="Enter name…" value={newName} onChange={function(e) { setNewName(e.target.value); }}
              onKeyDown={function(e) { if (e.key === 'Enter') handleAdd(); }} autoFocus data-testid="add-name-input" />
            <div className="flex gap-3">
              <button onClick={function() { setAddModal(null); setNewName(''); }}
                className="flex-1 py-2.5 rounded-xl text-sm font-medium border border-stone-200 text-stone-600 hover:bg-stone-50">Cancel</button>
              <button onClick={handleAdd} disabled={saving || !newName.trim()}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-50"
                style={{ background: BRAND }} data-testid="confirm-add-btn">
                {saving ? 'Adding…' : 'Add'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* REMOVE MODAL */}
      {removeModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-stone-800">Remove from Hierarchy</h3>
              <button onClick={function() { setRemoveModal(null); }} className="text-stone-400 hover:text-stone-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4">
              <p className="text-sm text-red-700">Remove <strong>{removeModal.name}</strong> ({removeModal.level})?</p>
              <p className="text-xs text-red-500 mt-1">This will also remove all CPs listed under them.</p>
            </div>
            <div className="flex gap-3">
              <button onClick={function() { setRemoveModal(null); }}
                className="flex-1 py-2.5 rounded-xl text-sm font-medium border border-stone-200 text-stone-600 hover:bg-stone-50">Cancel</button>
              <button onClick={handleRemove} disabled={saving}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                data-testid="confirm-remove-btn">
                {saving ? 'Removing…' : 'Yes, Remove'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CPStructureTab;
